import {
	PlaintiffData,
	RequiredField,
	SignData,
	SignDocumentDetails,
} from '@/interfaces';
import {
	getDeviceInfo,
	getUserIp,
	getFormattedTableDate,
	getUserRegion,
	isPdfEncrypted,
} from '@/lib';
import { FieldValues } from 'react-hook-form';
import { PDFDocument } from 'pdf-lib';
import { pdf } from '@react-pdf/renderer';
import SignaturePage from '@/components/dashboard/cases/SignaturePage';
import { format } from 'date-fns';
import { useSession } from 'next-auth/react';

export const useEditDocument = () => {
	const session = useSession();

	const getProxyUrl = (originalUrl: string): string => {
		if (
			originalUrl.includes('/api/') ||
			!originalUrl.includes('s3.us-east-1.amazonaws.com')
		) {
			return originalUrl;
		}
		return `/api/proxy/s3-proxy?url=${encodeURIComponent(originalUrl)}`;
	};

	const getTrackingData = async () => {
		const [ip, country] = await Promise.all([getUserIp(), getUserRegion()]);
		const { deviceType, os, browser } = getDeviceInfo();
		return {
			ip,
			device: deviceType,
			browser,
			country,
			os,
		};
	};

	const downloadSignedPdf = (
		pdfBlob: Blob,
		fileName = 'documento-firmado.pdf'
	) => {
		const url = URL.createObjectURL(pdfBlob);
		const a = document.createElement('a');
		a.href = url;
		a.download = fileName;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	};

	const getFieldQuestions = (data: FieldValues, fields: RequiredField[]) => {
		if (!data) {
			return {
				answers: undefined,
				plaintiffInfo: [],
			};
		}

		let plaintiffInfo: PlaintiffData[] = [];
		let answers: { id: string; response: string }[] = [];

		const fieldValues = Object.entries(data).filter(([key]) =>
			key.startsWith('field')
		);
		if (fieldValues.length > 0) {
			answers = fieldValues.map(([key, value]) => ({
				id: key.substring(6),
				response: value,
			}));

			plaintiffInfo = answers.map((a) => {
				const question = fields.find((f) => f.question_id === a.id)?.question;
				return {
					...a,
					question: question ?? 'No question found',
				};
			});
		}

		return {
			answers,
			plaintiffInfo,
		};
	};

	const fetchPdfAsArrayBuffer = async (
		pdfUrl: string
	): Promise<ArrayBuffer> => {
		try {
			const response = await fetch(pdfUrl);

			if (!response.ok) {
				throw new Error(
					`Error downloading the PDF: ${response.status} ${response.statusText}`
				);
			}

			const arrayBuffer = await response.arrayBuffer();
			return arrayBuffer;
		} catch (error) {
			console.error('Error fetching PDF:', error);
			throw error;
		}
	};

	const addSignaturePageToPdf = async (
		pdfBlob: Blob,
		signatureData: SignData | undefined,
		plaintiffInfo: PlaintiffData[],
		hasExistingSignaturePage = false
	): Promise<Blob> => {
		try {
			const arrayBuffer = await pdfBlob.arrayBuffer();
			if (isPdfEncrypted(arrayBuffer)) {
				throw new Error(
					'This document is password-protected and cannot be signed. Please upload an unprotected version of the file.'
				);
			}
			const pdfDoc = await PDFDocument.load(arrayBuffer, {
				ignoreEncryption: true,
			});
			// The fetched doc may already carry a signature page from a previous
			// submission of this same document, drop it so we don't stack pages.
			if (hasExistingSignaturePage && pdfDoc.getPageCount() > 1) {
				pdfDoc.removePage(pdfDoc.getPageCount() - 1);
			}
			const signatureReactPdf = (
				<SignaturePage data={signatureData} plaintiffInfo={plaintiffInfo} />
			);
			const signaturePdfBlob = await pdf(signatureReactPdf).toBlob();
			const signatureArrayBuffer = await signaturePdfBlob.arrayBuffer();
			const signaturePdfDoc = await PDFDocument.load(signatureArrayBuffer);
			const combinedPdf = await PDFDocument.create();

			// Copiar todas las páginas del original
			const originalPages = await combinedPdf.copyPages(
				pdfDoc,
				pdfDoc.getPageIndices()
			);
			originalPages.forEach((page) => combinedPdf.addPage(page));
			// Copiar la página de firma
			const signaturePages = await combinedPdf.copyPages(
				signaturePdfDoc,
				signaturePdfDoc.getPageIndices()
			);
			signaturePages.forEach((page) => combinedPdf.addPage(page));

			// 4. Guardar el PDF combinado
			const combinedPdfBytes = await combinedPdf.save();

			// Convert to a Uint8Array backed by a concrete ArrayBuffer to satisfy Blob's typings
			const uint8Array = Uint8Array.from(combinedPdfBytes as any);
			return new Blob([uint8Array], { type: 'application/pdf' });
		} catch (error) {
			console.error('Error adding signature page to PDF:', error);
			if (
				error instanceof Error &&
				error.message.includes('password-protected')
			) {
				throw error;
			}
			throw new Error('Could not add the signature page to the document');
		}
	};

	const setPageInfoToPdf = async (
		data: FieldValues,
		plaintiffInfo: PlaintiffData[],
		isApprove: boolean,
		actionDetails: SignDocumentDetails | undefined,
		hasExistingSignaturePage = false
	) => {
		if (!isApprove) return undefined;

		const arrayBuffer = await fetchPdfAsArrayBuffer(
			getProxyUrl(actionDetails?.url_doc ?? '')
		);
		const pdfBlob = new Blob([arrayBuffer], { type: 'application/pdf' });
		let signatureData: SignData | undefined = undefined;
		const trackingData = await getTrackingData();
		if (actionDetails) {
			const rawDate = new Date();
			const formatted = format(rawDate, 'yyyy-MM-dd HH:mm:ss');
			signatureData = {
				documentType: actionDetails.name_doc,
				created: getFormattedTableDate(actionDetails.created_at),
				timestamp: formatted,
				userId: session.data?.user.user.id,
				...trackingData,
				...(data.signature && {
					name: data.firstName
						? `${data.firstName} ${data.lastName}`
						: data.name,
					signature: data.signature,
					date: data.date,
					signed: actionDetails.hide_signing_date
						? undefined
						: format(data.date, 'MM-dd-yyyy'),
				}),
			};
		}

		const namedLabels: Record<string, string> = {
			firstName: 'First Name',
			lastName: 'Last Name',
			signature: 'Signature',
			date: 'Date',
		};
		const namedEntries: PlaintiffData[] = Object.entries(namedLabels)
			.filter(([key]) => data[key])
			.map(([key, label]) => ({
				id: key,
				question: label,
				response: data[key],
			}));

		const combinedPlaintiffInfo = [...namedEntries, ...plaintiffInfo];
		if (!signatureData && combinedPlaintiffInfo.length === 0) return undefined;
		return await addSignaturePageToPdf(
			pdfBlob,
			signatureData,
			combinedPlaintiffInfo,
			hasExistingSignaturePage
		);
	};

	return {
		getTrackingData,
		setPageInfoToPdf,
		getFieldQuestions,
		downloadSignedPdf,
	};
};
